import { useEffect, useState } from "react";
import cn from "../lib/cn";

const HeroSection = ({
    bgImage,
    frontImage,
    children,
    frontClass = "",
    className = "",
    contentClass = "",
}) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Trigger animations after component mounts
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <section
            className={cn(
                "relative py-[6.5rem] lg:py-[8rem] text-white bg-no-repeat bg-cover bg-center min-h-[70vh] flex items-center overflow-hidden",
                className
            )}
            style={{ backgroundImage: `url(${bgImage})` }}
            aria-label="Hero section featuring Prof. Bihari Lal Sharma"
            role="banner"
        >
            {/* Enhanced gradient overlay with darker background for golden text contrast */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-heritage-maroon/80 to-black/75"></div>
            
            {/* Subtle pattern overlay for texture */}
            <div className="absolute inset-0 opacity-15 bg-gradient-to-r from-transparent via-heritage-gold/30 to-transparent"></div>
            
            <div className="relative z-10 container  w-full mx-auto px-2 sm:px-4 lg:px-8">
                <div
                    className={cn(
                        "flex flex-col lg:flex-row justify-center w-full items-center gap-4 sm:gap-8 lg:gap-12",
                        frontClass
                    )}
                >
                    {/* Professional Portrait with Enhanced Framing */}
                    {frontImage && (
                        <div className={cn(
                            "flex-shrink-0 transform transition-all duration-500 ease-out",
                            isVisible ? "translate-y-0 opacity-100 scale-100" : "translate-y-8 opacity-0 scale-95"
                        )}>
                            <div className="relative">
                                {/* Golden border frame */}
                                <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-br from-heritage-gold via-primary-400 to-heritage-gold rounded-full opacity-80 blur-sm"></div>
                                <div className="absolute -inset-0.5 sm:-inset-1 bg-gradient-to-br from-heritage-gold to-primary-500 rounded-full"></div>
                                
                                <img
                                    src={frontImage}
                                    alt="Prof. Bihari Lal Sharma - Vice Chancellor of Sampurnanand Sanskrit University"
                                    className="relative w-32 h-32 sm:w-48 sm:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 rounded-full shadow-heritage object-cover border-2 sm:border-4 border-heritage-ivory/20"
                                    loading="eager"
                                    decoding="async"
                                />
                                
                                {/* Subtle glow effect */}
                                <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent to-white/10"></div>
                            </div>
                        </div>
                    )}
                    
                    {/* Enhanced Content Area */}
                    <div className={cn(
    "flex-1 text-center lg:text-left space-y-2 sm:space-y-4 lg:space-y-6 max-w-3xl px-2 sm:px-0",
    "break-words overflow-hidden",
    contentClass
)}>
                        {/* Animated text reveals */}
                        <div className={cn(
                            "transform transition-all duration-700 ease-out delay-200",
                            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                        )}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-heritage-gold via-primary-500 to-heritage-gold opacity-60"></div>
        </section>
    );
};

export default HeroSection;
